// cleanup_remove_external_libraries_GENERICS_IMPORT_MAP
// cleanup_convert_to_typescript_file_GENERICS_IMPORT_MAP

import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { template } from 'lodash';

import { Pagination, defaultStrings } from '@glu/data-components';
import gluLocale from '@glu/locale';

import { limitPageCount } from 'components/basicGrid/utils';
import useLocale from 'components/useLocale';

export const PAGE_SIZE = 'pageSize';
export const PAGE = 'page';
const defaultPerPage = 10;

gluLocale.set(defaultStrings);

export const ImportMappingTable = ({
  classes: {
    headerContentWrapper,
    highlightTopCheckboxes,
    noInformationFound,
    removeAccountTypeColumn,
    scrollBlock,
    tableContainer,
    tableWrapper
  },
  columns,
  data,
  messageRow,
  name,
  perPage: perPageFromProps,
  printMode,
  readonly,
  rowKey,
  search,
  showAccountTypeColumn,
  showFooter
}) => {
  const tableRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [perPage, setPerPage] = useState(perPageFromProps || defaultPerPage);
  const locale = useLocale();

  const currentPageToSet = printMode ? 0 : currentPage;
  const perPageToSet = printMode ? data.length : perPage;
  const currPos = currentPageToSet * perPageToSet;
  const filteredData = showFooter ? data.slice(currPos, currPos + perPageToSet) : data;
  const columnGroups = (columns || []).some((col) => 'columnGroup' in col);

  const goToPage = useCallback(
    (currentPageParam) => {
      if (!printMode) {
        setCurrentPage(currentPageParam);
      }
    },
    [printMode]
  );

  const perPageChangeHandler = useCallback(
    (perPageParam) => {
      if (!printMode) {
        setCurrentPage(0);
        setPerPage(perPageParam);
      }
    },
    [printMode]
  );

  const onPaginationChange = ({ name, value }) => {
    switch (name) {
      case PAGE:
        return goToPage(value);
      case PAGE_SIZE:
        return perPageChangeHandler(value);
      default:
        return null;
    }
  };

  useEffect(() => {
    const rowCount = (tableRef.current?.querySelectorAll('tr') || []).length;
    const colCount = (tableRef.current?.querySelectorAll('thead th') || []).length;
    tableRef.current?.setAttribute(
      'aria-label',
      template(locale['common.grid.ariaLabel'])({
        colCount,
        rowCount,
        label: locale['assignServices.title']
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!search) {
      perPageChangeHandler(defaultPerPage);
    } else {
      goToPage(0);
    }
  }, [goToPage, perPageChangeHandler, search]);

  return (
    <>
      <section
        className={classnames(tableContainer, {
          'account-type-column': showAccountTypeColumn,
          'no-checkbox': readonly,
          print: printMode,
          [removeAccountTypeColumn]: !showAccountTypeColumn
        })}
      >
        <section
          className={classnames(scrollBlock, 'scroll-block', {
            'no-account-type-column-adjustment': !showAccountTypeColumn,
            'no-checkbox': readonly
          })}
        >
          <div
            className={classnames(tableWrapper, {
              [highlightTopCheckboxes]: filteredData.length,
              'no-account-type-column-header-adjustment': showAccountTypeColumn
            })}
          >
            {filteredData.length === 0 && (
              <div className={noInformationFound} data-qa="no-information-import-mapping-table">
                <span>{locale['importMapping.message.noInformation']}</span>
              </div>
            )}
            <table className="table" data-qa={name} ref={tableRef}>
              <thead>
                {columnGroups && (
                  <tr>
                    {columns.map(({ columnHeading, columnSpan, noColumnHeading }) => (
                      <Fragment key={`column-${columnHeading}`}>
                        {!noColumnHeading && (
                          <th colSpan={columnSpan} className="noBorder">
                            {columnHeading || ''}
                          </th>
                        )}
                      </Fragment>
                    ))}
                  </tr>
                )}
                <tr>
                  {columns.map(({ content, field, title }) => (
                    <th key={field} className={`colNumber-${columns.length - 1}`}>
                      {title}
                      <div
                        className={classnames(!readonly && 'additionalHeader', {
                          [headerContentWrapper]: !filteredData.length
                        })}
                      >
                        {filteredData.length ? content : ''}
                      </div>
                    </th>
                  ))}
                </tr>
                {messageRow && messageRow.message && (
                  <tr className="messageRow">
                    <th colSpan={columns.length}>{messageRow.message}</th>
                  </tr>
                )}
              </thead>
              <tbody>
                {filteredData.map((item, index) => {
                  const idx = `${item[rowKey]}-${index}`;
                  return (
                    <tr key={idx}>
                      {columns.map(({ field, formatter }) => (
                        <td key={field} className={`colNumber-${columns.length - 1}`}>
                          {formatter ? formatter(item[field], item, field, index) : item[field]}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </section>
      {!printMode && showFooter && (
        <Pagination
          className="pagination-block"
          data={{
            page: currentPageToSet,
            pageSize: perPageToSet
          }}
          onChange={onPaginationChange}
          parsePageSizeOptions={limitPageCount(locale)}
          totalItems={data.length}
          htmlIdRoot="import-mapping-table"
        />
      )}
    </>
  );
};

ImportMappingTable.defaultProps = {
  classes: {},
  columns: [],
  data: [],
  messageRow: {},
  name: '',
  perPage: 10,
  printMode: false,
  readonly: false,
  rowKey: '',
  search: '',
  showAccountTypeColumn: false,
  showFooter: false
};

ImportMappingTable.propTypes = {
  classes: PropTypes.shape({}),
  columns: PropTypes.arrayOf(PropTypes.object),
  data: PropTypes.arrayOf(PropTypes.object),
  messageRow: PropTypes.shape({}),
  name: PropTypes.string,
  perPage: PropTypes.number,
  printMode: PropTypes.bool,
  rowKey: PropTypes.string,
  readonly: PropTypes.bool,
  search: PropTypes.string,
  showAccountTypeColumn: PropTypes.bool,
  showFooter: PropTypes.bool
};

export default ImportMappingTable;
